import React, { useState } from 'react';
import { Download, FileText, Edit, Brain, Target, BookOpen, MessageCircle, Wand2, GraduationCap, BarChart3 } from 'lucide-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

import EssaySamples from './components/EssaySamples';
import TemplateWizard from './components/TemplateWizard';
import GeneratedEssay from './components/GeneratedEssay';
import FlashCardsGenerator from './components/FlashCardsGenerator';
import EssayQuiz from './components/EssayQuiz';
import HKDSETexts from './components/HKDSETexts';
import ChineseEssayWritingToolbox from './components/ChineseEssayWritingToolbox';
import DeepSeekChatWindow from './components/DeepSeekChatWindow';
import PrimaryChinese from './components/PrimaryChinese';
import KnowledgeGraph from './components/KnowledgeGraph';
import TextSizeControl from './components/TextSizeControl';
import { TextSizeProvider, useTextSize } from './contexts/TextSizeContext';
import { essayTemplates, categories, templateTypes, usefulExpressions } from './data/essayTemplates';

const AppContent = () => {
  const { textSize, setTextSize, tabLabelMode } = useTextSize();
  const [activeMainTab, setActiveMainTab] = useState('writing');
  const [activeSubTab, setActiveSubTab] = useState('samples');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEssayForFlashCards, setSelectedEssayForFlashCards] = useState(null);
  const [selectedEssayForQuiz, setSelectedEssayForQuiz] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [templateData, setTemplateData] = useState({
    type: 'narrative',
    recipient: '',
    sender: '',
    date: '',
    subject: '',
    greeting: '',
    opening: '',
    mainBody: '',
    conclusion: '',
    closing: ''
  });

  // Filter samples based on search and category
  const filteredSamples = essayTemplates.filter(sample => {
    const matchesCategory = selectedCategory === 'all' || sample.category === selectedCategory;
    const matchesSearch = sample.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sample.category.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleTemplateChange = (field, value) => {
    setTemplateData(prev => ({ ...prev, [field]: value }));
  };

  // Autofill function to populate wizard with sample data
  const autofillFromSample = (sample) => {
    setTemplateData({
      type: sample.type,
      recipient: sample.content.recipient,
      sender: sample.content.sender,
      date: sample.content.date,
      subject: sample.content.subject,
      greeting: sample.content.greeting,
      opening: sample.content.opening,
      mainBody: sample.content.mainBody,
      conclusion: sample.content.conclusion,
      closing: sample.content.closing
    });
    setActiveSubTab('wizard');
  };

  const generateFlashCardsFromEssay = (essay) => {
    setSelectedEssayForFlashCards(essay);
    setActiveSubTab('flashcards');
  };

  const startQuizFromEssay = (essay) => {
    setSelectedEssayForQuiz(essay);
    setActiveSubTab('quiz');
  };

  // Reset template to initial state
  const resetTemplate = () => {
    setTemplateData({
      type: 'narrative',
      recipient: '',
      sender: '',
      date: '',
      subject: '',
      greeting: '',
      opening: '',
      mainBody: '',
      conclusion: '',
      closing: ''
    });
  };

  // Enhanced PDF generation using html2canvas for Chinese character support
  const generatePDF = async () => {
    try {
      // Create a temporary container with the essay content
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '210mm'; // A4 width
      tempContainer.style.padding = '20mm';
      tempContainer.style.fontFamily = 'Arial, "Microsoft YaHei", "SimSun", sans-serif';
      tempContainer.style.fontSize = '14px';
      tempContainer.style.lineHeight = '1.6';
      tempContainer.style.color = '#333';
      tempContainer.style.backgroundColor = 'white';

      // Get template type label
      const typeLabel = templateTypes.find(t => t.value === templateData.type)?.label || templateData.type;

      // Create the HTML content
      tempContainer.innerHTML = `
        <div style="max-width: 170mm; margin: 0 auto;">
          <!-- Header -->
          <div style="text-align: center; margin-bottom: 30px; padding-bottom: 15px; border-bottom: 2px solid #333;">
            <h1 style="font-size: 18px; font-weight: bold; margin: 0 0 10px 0; color: #333;">
              ${typeLabel} 作文
            </h1>
          </div>

          <!-- Document Info -->
          <div style="margin-bottom: 25px; font-size: 12px; color: #666;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px;">
              <div><strong>日期：</strong> ${templateData.date || '[日期]'}</div>
              <div><strong>主旨：</strong> ${templateData.subject || '[主旨]'}</div>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
              <div><strong>收件人：</strong> ${templateData.recipient || '[收件人]'}</div>
              <div><strong>寄件人：</strong> ${templateData.sender || '[寄件人]'}</div>
            </div>
          </div>

          <!-- Content -->
          <div style="space-y: 20px;">
            <!-- Greeting -->
            <div style="margin-bottom: 20px;">
              <div style="color: #2563eb; font-weight: 600; font-size: 16px; margin-bottom: 5px;">
                ${templateData.greeting || '[問候語]'}
              </div>
            </div>

            <!-- Opening -->
            <div style="margin-bottom: 20px;">
              <h3 style="font-size: 12px; font-weight: bold; color: #059669; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px;">
                開頭段落
              </h3>
              <p style="margin: 0; text-indent: 2em; line-height: 1.8;">
                ${templateData.opening || '[開頭段落 - 說明您的寫作目的]'}
              </p>
            </div>

            <!-- Main Body -->
            <div style="margin-bottom: 20px;">
              <h3 style="font-size: 12px; font-weight: bold; color: #7c3aed; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px;">
                主要內容
              </h3>
              <div style="margin: 0; text-indent: 2em; line-height: 1.8; white-space: pre-line;">
                ${templateData.mainBody || '[主要內容 - 以連貫、有條理的段落呈現您的詳細內容]'}
              </div>
            </div>

            <!-- Conclusion -->
            <div style="margin-bottom: 20px;">
              <h3 style="font-size: 12px; font-weight: bold; color: #ea580c; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px;">
                結論段落
              </h3>
              <p style="margin: 0; text-indent: 2em; line-height: 1.8;">
                ${templateData.conclusion || '[結論段落 - 禮貌的最後訊息和結尾備註]'}
              </p>
            </div>

            <!-- Closing -->
            <div style="margin-top: 30px; text-align: right;">
              <div style="color: #dc2626; font-weight: 600; white-space: pre-line;">
                ${templateData.closing || '[結尾敬語 - 應與稱呼相符]'}
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div style="margin-top: 40px; text-align: center; font-size: 10px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 15px;">
            由中文作文助手生成 - ${new Date().toLocaleDateString('zh-TW')}
          </div>
        </div>
      `;

      // Add to document temporarily
      document.body.appendChild(tempContainer);

      // Wait a moment for fonts to load
      await new Promise(resolve => setTimeout(resolve, 100));

      // Convert to canvas
      const canvas = await html2canvas(tempContainer, {
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        backgroundColor: 'white',
        width: tempContainer.offsetWidth,
        height: tempContainer.offsetHeight
      });

      // Remove temporary container
      document.body.removeChild(tempContainer);

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      // Add image to PDF
      if (imgHeight <= pdfHeight) {
        // Single page
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      } else {
        // Multiple pages
        let position = 0;
        const pageHeight = pdfHeight;
        
        while (position < imgHeight) {
          if (position > 0) {
            pdf.addPage();
          }
          
          pdf.addImage(imgData, 'PNG', 0, -position, imgWidth, imgHeight);
          position += pageHeight;
        }
      }

      // Generate filename
      const date = new Date().toISOString().split('T')[0];
      const fileName = `${typeLabel}-${templateData.subject || '作文'}-${date}.pdf`;

      // Save the PDF
      pdf.save(fileName);

      // Show success message
      const message = document.createElement('div');
      message.style.position = 'fixed';
      message.style.top = '20px';
      message.style.right = '20px';
      message.style.backgroundColor = '#10b981';
      message.style.color = 'white';
      message.style.padding = '12px 20px';
      message.style.borderRadius = '8px';
      message.style.zIndex = '9999';
      message.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
      message.textContent = 'PDF 已成功下載！';
      
      document.body.appendChild(message);
      
      setTimeout(() => {
        if (document.body.contains(message)) {
          document.body.removeChild(message);
        }
      }, 3000);

    } catch (error) {
      console.error('PDF generation error:', error);
      
      // Show error message
      const message = document.createElement('div');
      message.style.position = 'fixed';
      message.style.top = '20px';
      message.style.right = '20px';
      message.style.backgroundColor = '#ef4444';
      message.style.color = 'white';
      message.style.padding = '12px 20px';
      message.style.borderRadius = '8px';
      message.style.zIndex = '9999';
      message.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
      message.textContent = 'PDF 生成失敗，請稍後再試。';
      
      document.body.appendChild(message);
      
      setTimeout(() => {
        if (document.body.contains(message)) {
          document.body.removeChild(message);
        }
      }, 3000);
    }
  };

  // Main tab configuration
  const mainTabs = [
    { id: 'writing', label: '中文寫作', icon: FileText },
    { id: 'primary', label: '小學中文', icon: GraduationCap },
    { id: 'hkdse', label: 'HKDSE 十二篇範文', icon: BookOpen }
  ];

  // Sub tab configuration for Chinese Writing
  const writingSubTabs = [
    { id: 'samples', label: '作文範例', icon: FileText },
    { id: 'knowledge-graph', label: 'Knowledge Graph', icon: BarChart3 },
    { id: 'toolbox', label: '中文寫作工具箱', icon: Wand2 },
    { id: 'flashcards', label: '學習卡片', icon: Brain },
    { id: 'quiz', label: '作文測驗', icon: Target },
    { id: 'wizard', label: '範本編輯器', icon: Edit },
    { id: 'generated', label: '生成作文', icon: Download }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Edit className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">中文作文助手</h1>
                <p className="text-sm text-gray-600">運用人工智能幫助創作優秀的中文作文</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Navigation Tabs */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8">
            {mainTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveMainTab(tab.id);
                    if (tab.id === 'writing') {
                      setActiveSubTab('samples');
                    }
                  }}
                  className={`flex items-center ${tabLabelMode==='icon' ? '' : 'space-x-2'} py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeMainTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tabLabelMode==='icon_text' && <span>{tab.label}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Sub Navigation Tabs - Only show for Chinese Writing */}
      {activeMainTab === 'writing' && (
        <nav className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex space-x-6">
              {writingSubTabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveSubTab(tab.id)}
                    className={`flex items-center ${tabLabelMode==='icon' ? '' : 'space-x-2'} py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeSubTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tabLabelMode==='icon_text' && <span>{tab.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Chinese Writing Tab Content */}
        {activeMainTab === 'writing' && (
          <>
            {/* Essay Samples Sub Tab */}
            {activeSubTab === 'samples' && (
              <EssaySamples
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                categories={categories}
                filteredSamples={filteredSamples}
                autofillFromSample={autofillFromSample}
                generateFlashCardsFromEssay={generateFlashCardsFromEssay}
                startQuizFromEssay={startQuizFromEssay}
              />
            )}

            {/* Knowledge Graph Sub Tab */}
            {activeSubTab === 'knowledge-graph' && (
              <KnowledgeGraph />
            )}

            {/* Chinese Writing Toolbox Sub Tab */}
            {activeSubTab === 'toolbox' && (
              <ChineseEssayWritingToolbox />
            )}

            {/* Flash Cards Sub Tab */}
            {activeSubTab === 'flashcards' && (
              <FlashCardsGenerator
                onBack={() => {
                  setSelectedEssayForFlashCards(null);
                  setActiveSubTab('samples');
                }}
                selectedEssay={selectedEssayForFlashCards}
              />
            )}

            {/* Quiz Sub Tab */}
            {activeSubTab === 'quiz' && (
              <EssayQuiz
                selectedEssay={selectedEssayForQuiz}
                onBack={() => {
                  setSelectedEssayForQuiz(null);
                  setActiveSubTab('samples');
                }}
              />
            )}

            {/* Template Wizard Sub Tab */}
            {activeSubTab === 'wizard' && (
              <TemplateWizard
                templateData={templateData}
                handleTemplateChange={handleTemplateChange}
                templateTypes={templateTypes}
                usefulExpressions={usefulExpressions}
                resetTemplate={resetTemplate}
                setActiveTab={setActiveSubTab}
              />
            )}

            {/* Generated Essay Sub Tab */}
            {activeSubTab === 'generated' && (
              <GeneratedEssay 
                templateData={templateData} 
                generatePDF={generatePDF}
                handleTemplateChange={handleTemplateChange}
              />
            )}
          </>
        )}

        {/* Primary Chinese Tab Content */}
        {activeMainTab === 'primary' && (
          <PrimaryChinese />
        )}

        {/* HKDSE Texts Tab Content */}
        {activeMainTab === 'hkdse' && (
          <HKDSETexts />
        )}
      </div>

      {/* Floating Chat Button */}
      {!isChatOpen && (
        <button
          onClick={() => setIsChatOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center z-40 hover:scale-110"
          title="AI助手"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* DeepSeek Chat Window */}
      <DeepSeekChatWindow
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        contextData={null}
        initialMode="direct"
      />

      {/* Text Size Control */}
      <TextSizeControl 
        textSize={textSize} 
        onTextSizeChange={setTextSize} 
      />
    </div>
  );
};

const App = () => {
  return (
    <TextSizeProvider>
      <AppContent />
    </TextSizeProvider>
  );
};

export default App;
